import React, { useState, useEffect, useRef } from 'react';

import {

  Linkedin,

  Mail,

  ArrowRight,

  Menu,

  X,

  ShieldCheck,

  LineChart,
  Home,
  Brain,
  Star,
  Trophy,
  Github,
  ExternalLink
} from 'lucide-react';

import {

  motion,

  AnimatePresence,

  useInView

} from 'framer-motion';



// --- Framer Motion Animation Variants ---

const sectionVariant = {

  hidden: { opacity: 0, y: 50 },

  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.6, 0.05, 0.01, 0.9] } }

};



const cardGridVariant = {

  hidden: { opacity: 0 },

  visible: { opacity: 1, transition: { staggerChildren: 0.2 } }

};



const cardVariant = {

  hidden: { opacity: 0, y: 30 },

  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }

};



// Custom Hook for triggering animations on scroll

function useAnimatedSection(threshold = 0.2) {

  const ref = useRef(null);

  const isInView = useInView(ref, { once: true, amount: threshold });

  return { ref, variants: sectionVariant, initial: "hidden", animate: isInView ? "visible" : "hidden" };

}



// Custom Substack Icon Component

const SubstackIcon = ({ size = 32, ...props }) => (

  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" width={size} height={size} {...props}>

    <path d="M22.532 2.468L1.468 9.532V21.5h21.064V2.468zM4.532 18.436V11.8l14.936-5.264v11.9z" />

    <path d="M4.532 9.532L19.468 4.2V2.468L1.468 9.532z" />

  </svg>

);



// --- Data Definitions ---

const allProjects = [
  {
    name: 'DataGuard – Google DeepMind x Cactus Hackathon (2nd Place)',
    summary: 'A concept for detecting and preventing sensitive data exposure in AI systems using hybrid cloud architecture and on-device inference.',
    description: 'Built a concept for detecting and preventing sensitive data exposure in AI systems using hybrid cloud architecture, model-level security checks, and on-device inference using Google DeepMind FunctionalGemma SLM, Gemini and Cactus Models.',
    tags: ['GenAI', 'Security', 'Hackathon'],
    links: [
      { label: 'LinkedIn Post', url: 'https://shorturl.at/YIclp', icon: 'external' },
      { label: 'GitHub', url: 'http://github.com/Rayhanpatel/functiongemma-hackathon', icon: 'github' },
    ],
  },
  {
    name: 'PGConnect: Cloud Force x Microsoft Hack4Impact – 1st Place',
    summary: "An AI-powered service agent that helps Prince George's County residents find the right department, contacts, and resources using plain language.",
    description: "PGConnect is an AI-powered service agent that enhances Prince George's County 311 by acting as an intelligent access layer for county services. Residents facing health concerns, cybersecurity incidents, scams, or online safety issues often struggle to find the right reporting channels. PGConnect allows residents to describe their issue in plain language and instantly receive the correct department, verified contact information, reporting links, and actionable resources.",
    tags: ['GenAI', 'Agent', 'Hackathon'],
    links: [
      { label: 'Live Demo', url: 'https://pg-connect-xi.vercel.app/', icon: 'external' },
      { label: 'LinkedIn Post', url: 'https://tinyurl.com/33kpc6ux', icon: 'external' },
    ],
  },
  {
    name: 'GovForms',
    summary: 'An AI-powered platform that simplifies complex government form filings like SF330, SF254, and SF255 for construction and A-E firms.',
    description: 'GovForms is a Construction / A-E focused platform designed to simplify complex government form filings such as SF330, SF254, and SF255. These forms are often time-consuming, repetitive, and prone to errors. GovForms uses AI to guide users through the process, reuse firm data across submissions, and structure information so teams can prepare compliant submissions faster and with fewer mistakes. Developed a prototype that later served as a reference for building the complete product. Handled product and user research, design, development/engineering, AI integrations, and security.',
    tags: ['Full Stack', 'Product', 'GenAI'],
    links: [
      { label: 'Live Demo', url: 'https://gov-forms-seven.vercel.app/', icon: 'external' },
    ],
  },
  {
    name: 'PKI Infrastructure Overhaul',
    summary: 'Led a large-scale PKI modernization project at a healthcare company, automating certificate workflows and reducing SLAs.',
    description: 'Led a large-scale PKI modernization project at a healthcare company. Automated certificate workflows using Venafi TPM and HSMs, removing manual intervention and significantly reducing SLAs.',
    tags: ['Security', 'PKI', 'IAM'],
    links: [],
  },
];



const allPosts = [
  {
    title: 'The Greater Fool',
    content: 'Being a “greater fool” isn’t about failure, it’s about daring to believe when no one else does. This blog is a reflection on chasing dreams, taking risks, and finding meaning in the pursuit itself.',
    tags: ['Startups', 'Personal'],
    category: 'Personal',
    links: [
      { label: 'Read on Substack', url: 'https://rahulmarri.substack.com/p/the-greater-fool', icon: 'external' },
    ],
  },
  {
    title: 'A Chance',
    content: 'We all wait for jobs, for opportunities, for someone to notice our work. A Chance is a reflection on what it feels like to be ready but unseen, and why even a single opportunity can matter so deeply when it finally arrives.',
    tags: ['Personal', 'Inspiration'],
    category: 'Personal',
    links: [],
  },
  {
    title: 'Gaps in the SIEM Market',
    content: 'A technical analysis of current Security Information and Event Management solutions, identifying key operational gaps and market opportunities for new startups to innovate.',
    tags: ['Security', 'Technical'],
    category: 'Tech',
    links: [
      { label: 'Read Article', url: 'https://www.tejascybernetwork.com/post/gaps-in-the-siem-market-opportunities-for-future-founders', icon: 'external' },
    ],
  },
  {
    title: 'The Rise of AI-Powered Cyber Attacks',
    content: 'Examining the new frontier of cybersecurity threats where adversaries are leveraging generative AI to create more sophisticated and evasive attacks, and how defensive strategies must evolve.',
    tags: ['GenAI', 'Security'],
    category: 'Tech',
    links: [
      { label: 'Read Article', url: 'https://cybersecuritynews.com/the-next-frontier-in-digital-security-challenges/', icon: 'external' },
    ],
  },
];



// --- Core App Structure ---

export default function App() {

  const [currentPage, setCurrentPage] = useState('home');

  const [scrollTo, setScrollTo] = useState(null);



  const navigate = (page, section = null) => {

    setCurrentPage(page);

    setScrollTo(section);

    window.scrollTo(0, 0);

  };



  useEffect(() => {

    if (currentPage === 'home' && scrollTo) {

      setTimeout(() => {

        document.getElementById(scrollTo)?.scrollIntoView({ behavior: 'smooth' });

        setScrollTo(null);

      }, 100);

    }

  }, [currentPage, scrollTo]);



  const renderPage = () => {

    switch (currentPage) {

      case 'projects': return <ProjectsPage navigate={navigate} />;

      case 'blog': return <BlogPage navigate={navigate} />;

      default: return <HomePage navigate={navigate} />;

    }

  };



  return (

    <div className="bg-white text-gray-800 leading-relaxed tracking-wide">

      <Navbar navigate={navigate} currentPage={currentPage} />

      <main>{renderPage()}</main>

      <Footer />

    </div>

  );

}



// --- Page Components ---

const HomePage = ({ navigate }) => (

  <>

    <HeroSection />

    <SkillsSection />
    <AchievementsSection />

    <ContactSection />

  </>

);



const ProjectsPage = ({ navigate }) => (

  <motion.section id="projects-page" className="py-20 pt-28 bg-white" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>

    <div className="container mx-auto px-4 sm:px-6 lg:px-8">

      <div className="text-center mb-12">

        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 serif-font">Projects</h1>

        <p className="text-lg text-gray-600 mt-4">A collection of my recent work and explorations.</p>

      </div>

      <ProjectsGrid navigate={navigate} />

      <div className="text-center mt-16">

        <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={() => navigate('home')} className="inline-flex items-center px-6 py-3 bg-black text-white font-semibold rounded-lg shadow-lg hover:bg-gray-800 transition-colors">

          <Home className="mr-2" size={20} />

          Back to Home

        </motion.button>

      </div>

    </div>

  </motion.section>

);



const BlogPage = ({ navigate }) => (

  <motion.section id="blog-page" className="py-20 pt-28 bg-white" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>

    <div className="container mx-auto px-4 sm:px-6 lg:px-8">

      <div className="text-center mb-12">

        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 serif-font">From the Blog</h1>

        <p className="text-lg text-gray-600 mt-4">Thoughts on security, technology, startups, and life.</p>

      </div>

      <BlogGrid navigate={navigate} />

      <div className="text-center mt-16">

        <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={() => navigate('home')} className="inline-flex items-center px-6 py-3 bg-black text-white font-semibold rounded-lg shadow-lg hover:bg-gray-800 transition-colors">

          <Home className="mr-2" size={20} />

          Back to Home

        </motion.button>

      </div>

    </div>

  </motion.section>

);



// --- Shared Components ---

const Navbar = ({ navigate, currentPage }) => {

  const [isOpen, setIsOpen] = useState(false);

  const navLinks = [

    { title: 'Home', page: 'home', section: 'home' },

    { title: 'About', page: 'home', section: 'about' },

    { title: 'Skills', page: 'home', section: 'skills' },

    { title: 'Projects', page: 'projects' },

    { title: 'Blog', page: 'blog' },

    { title: 'Contact', page: 'home', section: 'contact' },

  ];

  const handleNavigate = (page, section) => {

    navigate(page, section);

    setIsOpen(false);

  };



  return (

    <header className="bg-white/80 backdrop-blur-sm sticky top-0 z-50 border-b border-gray-200">

      <div className="container mx-auto px-4 sm:px-6 lg:px-8">

        <div className="flex items-center justify-between h-20">

          <div className="flex-shrink-0">

            <button onClick={() => handleNavigate('home', 'home')} className="text-2xl font-bold text-gray-900 tracking-wider serif-font">

              Rahul Marri

            </button>

          </div>

          <nav className="hidden md:flex md:items-center md:space-x-8">

            {navLinks.map((link) => (

              <motion.button key={link.title} whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }} onClick={() => handleNavigate(link.page, link.section)} className={`transition-colors duration-300 font-medium ${currentPage === link.page && !link.section ? 'text-orange-600' : 'text-gray-600 hover:text-orange-600'}`}>

                {link.title}

              </motion.button>

            ))}

          </nav>

          <div className="md:hidden">

            <button onClick={() => setIsOpen(!isOpen)} className="text-gray-800 hover:text-orange-600 focus:outline-none">

              {isOpen ? <X size={28} /> : <Menu size={28} />}

            </button>

          </div>

        </div>

      </div>

      <AnimatePresence>

        {isOpen && (

          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="md:hidden overflow-hidden">

            <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white border-b border-gray-200">

              {navLinks.map((link) => (

                <button key={link.title} onClick={() => handleNavigate(link.page, link.section)} className="block w-full text-left px-3 py-2 rounded-md text-base font-medium text-gray-700 hover:text-orange-600 hover:bg-orange-50">

                  {link.title}

                </button>

              ))}

            </div>

          </motion.div>

        )}

      </AnimatePresence>

    </header>

  );

};



const HeroSection = () => (
  <section id="home" className="bg-slate-50 pt-32 pb-16 md:pt-40 md:pb-24">
    <div className="container mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div className="relative z-10 text-center max-w-4xl mx-auto" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2, ease: 'easeOut' }}>
        <motion.div className="w-40 h-40 md:w-48 md:h-48 rounded-full mx-auto mb-8 ring-4 ring-orange-400 ring-offset-4 overflow-hidden shadow-xl" initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.8, delay: 0.3 }}>
          <img alt="Rahul Marri" className="w-full h-full rounded-full object-cover" src="/images/profile-headshot.png" />
        </motion.div>
        <motion.h1 className="text-5xl md:text-7xl font-extrabold text-gray-900 mb-4 tracking-tight serif-font" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.4 }}>
          Hi, I'm Rahul
        </motion.h1>
        <motion.p className="text-lg md:text-xl text-gray-600 mt-4 mb-12 tracking-wide font-medium" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.6 }}>
          Security | Product | GenAI/ML | Startups
        </motion.p>

        <motion.div id="about" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.8 }} className="scroll-mt-24">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-8 serif-font text-center">About Me</h2>
          <div className="space-y-6 text-gray-700 text-lg text-justify leading-relaxed">
            <p>
              I’m Rahul — I work in security, but I’m really interested in how great products get built securely at scale. I’m always chasing the next idea worth building, and I spend a lot of time thinking about how security can enable products rather than slow them down.
            </p>
            <p>
              I’ve worked across enterprises and startups, from edtech to healthcare, helping teams build secure cloud environments, run security assessments, and meet compliance requirements. Over time I’ve touched many parts of security, but what I care about most is building the foundations that let teams move fast without breaking things. To me, security works best when it’s built into the product from the beginning, not added later as a constraint.
            </p>
            <p>
              Outside of security, I’ve always been drawn to startups and builder communities. During my bachelor’s, I started an Innovations & Startup Club so students could share ideas and learn from founders. At the University of Maryland, I carried that forward as a Graduate Student Senator and Vice President of the International Student Union, where I spent a lot of time connecting people who wanted to build, collaborate, and push ideas forward.
            </p>
            <p>
              I invested in CoffeeSpace, a platform helping founders find co-founders. I’ve seen how one good connection can completely change the trajectory of an idea.
            </p>
            <p>
              At the end of the day, I enjoy being around people solving hard problems and trying to build things that matter. Security just happens to be the lens through which I approach it, but the motivation is always the same: finding the next idea worth building and helping make the world a better place to live.
            </p>
          </div>
        </motion.div>
      </motion.div>
    </div>
  </section>
);

const SkillsSection = () => {

  const sectionAnimation = useAnimatedSection();

  const skills = {

    cybersecurity: ['AWS Cloud Security', 'Enterprise Security', 'Application Security', 'Security Assessments', 'Vulnerability Management', 'Security Operations', 'IAM, PAM, & PKI', 'Network Security', 'Compliance (SOC2, HIPAA, ISO)'],
    ai: ['AI Integrations', 'Agents', 'Workflows', 'RAG', 'AI Security'],
    product: ['Product Management', 'Growth', 'Operations', 'User Research'],
    interests: ['Security Engineering', 'AI Security', 'GenAI/ML', 'Product Management', 'Startups']
  };

  return (

    <motion.section id="skills" className="py-8 md:py-12 bg-gray-50" {...sectionAnimation}>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12 serif-font">Skills & Interests</h2>
        <div className="max-w-5xl mx-auto grid md:grid-cols-2 gap-x-12 gap-y-10">

          <div>

            <div className="flex items-center mb-6">

              <ShieldCheck className="text-orange-600 mr-4 flex-shrink-0" size={32} />

              <h3 className="text-2xl font-bold text-gray-800 serif-font">Cybersecurity</h3>

            </div>

            <motion.div className="flex flex-wrap gap-3" variants={cardGridVariant} initial="hidden" animate="visible">

              {skills.cybersecurity.map(skill => (

                <motion.span key={skill} className="bg-gray-200 text-gray-800 px-4 py-2 rounded-lg text-base font-medium" variants={cardVariant} whileHover={{ backgroundColor: "#FDBA74", color: "#1F2937" }}>

                  {skill}

                </motion.span>

              ))}

            </motion.div>

            <div className="flex items-center mb-6 mt-10">
              <Star className="text-orange-600 mr-4 flex-shrink-0" size={32} />
              <h3 className="text-2xl font-bold text-gray-800 serif-font">Interests</h3>
            </div>
            <motion.div className="flex flex-wrap gap-3" variants={cardGridVariant} initial="hidden" animate="visible">
              {skills.interests.map(interest => (
                <motion.span key={interest} className="bg-gray-200 text-gray-800 px-3 py-2 rounded-lg text-sm font-medium" variants={cardVariant} whileHover={{ backgroundColor: "#FDBA74", color: "#1F2937" }}>
                  {interest}
                </motion.span>
              ))}
            </motion.div>
          </div>

          <div>

            <div className="flex items-center mb-6">
              <Brain className="text-orange-600 mr-4 flex-shrink-0" size={32} />
              <h3 className="text-2xl font-bold text-gray-800 serif-font">AI/GenAI</h3>
            </div>
            <motion.div className="flex flex-wrap gap-3" variants={cardGridVariant} initial="hidden" animate="visible">
              {skills.ai.map(skill => (
                <motion.span key={skill} className="bg-gray-200 text-gray-800 px-3 py-2 rounded-lg text-sm font-medium" variants={cardVariant} whileHover={{ backgroundColor: "#FDBA74", color: "#1F2937" }}>
                  {skill}
                </motion.span>
              ))}
            </motion.div>

            <div className="flex items-center mb-6 mt-10">
              <LineChart className="text-orange-600 mr-4 flex-shrink-0" size={32} />

              <h3 className="text-2xl font-bold text-gray-800 serif-font">Product & Growth</h3>

            </div>

            <motion.div className="flex flex-wrap gap-3" variants={cardGridVariant} initial="hidden" animate="visible">

              {skills.product.map(skill => (

                <motion.span key={skill} className="bg-gray-200 text-gray-800 px-4 py-2 rounded-lg text-base font-medium" variants={cardVariant} whileHover={{ backgroundColor: "#FDBA74", color: "#1F2937" }}>

                  {skill}

                </motion.span>

              ))}

            </motion.div>

          </div>

        </div>

      </div>

    </motion.section>

  );

};

const AchievementsSection = () => {
  const sectionAnimation = useAnimatedSection();
  const achievements = [
    {
      title: '1st Place – Cloud Force x Microsoft Hack4Impact',
      description: "Built PGConnect, an AI-powered service agent for Prince George's County 311 that helps residents find the right department, contacts, and resources using plain language.",
      links: [
        { label: 'LinkedIn Post', url: 'https://tinyurl.com/33kpc6ux', icon: 'external' },
      ],
    },
    {
      title: '2nd Place – Google DeepMind x Cactus Hackathon',
      description: 'Built DataGuard, a concept for detecting and preventing sensitive data exposure in AI systems using hybrid cloud architecture and on-device inference with FunctionalGemma SLM.',
      links: [
        { label: 'LinkedIn Post', url: 'https://shorturl.at/YIclp', icon: 'external' },
      ],
    },
    {
      title: 'Mad About Ideas – Top 15 & XLr8AP Startup Accelerator',
      description: 'Placed in the top 15 out of 2,500 submissions with an e-commerce platform for artisans and was selected for the XLr8AP state accelerator by the Government of Andhra Pradesh.',
      links: [
        { label: 'Read “StartAP Fest”', url: 'https://www.thehindu.com/news/cities/Vijayawada/StartAP-fest-off-to-a-breezy-start/article17326828.ece', icon: 'external' },
        { label: 'Read “MAI Challenge”', url: 'https://www.thehindu.com/news/cities/Visakhapatnam/MAI-%E2%80%98challenge%E2%80%99-to-foster-entrepreneurial-ecosystem/article16643644.ece', icon: 'external' },
      ],
    },
  ];

  return (
    <motion.section id="achievements" className="py-8 md:py-12 bg-white" {...sectionAnimation}>
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12 serif-font">Achievements</h2>
        <motion.div className="max-w-4xl mx-auto flex flex-col gap-6" variants={cardGridVariant} initial="hidden" animate="visible">
          {achievements.map((item, index) => (
            <motion.div key={index} className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl border border-gray-200 group" variants={cardVariant} whileHover={{ y: -4, transition: { duration: 0.3 } }}>
              <div className="p-6 md:p-8">
                <div className="flex items-start gap-3 mb-3">
                  <Trophy className="text-orange-600 flex-shrink-0 mt-1" size={24} />
                  <h3 className="text-xl md:text-2xl font-bold text-gray-900 serif-font group-hover:text-orange-600 transition-colors duration-300">{item.title}</h3>
                </div>
                <p className="text-gray-600 text-base leading-relaxed ml-9">{item.description}</p>
                {item.links && item.links.length > 0 && (
                  <div className="flex flex-wrap gap-4 mt-5 pt-5 border-t border-gray-100">
                    {item.links.map((link, idx) => (
                      <a key={idx} href={link.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center text-orange-600 font-semibold hover:text-orange-800 transition-colors duration-300 text-sm">
                        {link.icon === 'github' ? <Github size={16} className="mr-1.5" /> : <ExternalLink size={16} className="mr-1.5" />}
                        {link.label}
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </motion.section>
  );
};



const ProjectCard = ({ name, summary, description, tags, links, brief = false }) => (

  <motion.div
    className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl border border-gray-200 group"
    variants={cardVariant}
    whileHover={{ y: -4, transition: { duration: 0.3 } }}
  >

    <div className="p-6 md:p-8">

      <div className="flex flex-wrap gap-2 mb-4">
        {tags.map((tag) => (<span key={tag} className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full font-medium">{tag}</span>))}
      </div>

      <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3 serif-font group-hover:text-orange-600 transition-colors duration-300">{name}</h3>

      <p className="text-gray-600 text-base leading-relaxed">{brief ? summary : description}</p>

      {!brief && links && links.length > 0 && (
        <div className="flex flex-wrap gap-4 mt-5 pt-5 border-t border-gray-100">
          {links.map((link, idx) => (
            <a
              key={idx}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-orange-600 font-semibold hover:text-orange-800 transition-colors duration-300 text-sm"
            >
              {link.icon === 'github' ? <Github size={16} className="mr-1.5" /> : <ExternalLink size={16} className="mr-1.5" />}
              {link.label}
            </a>
          ))}
        </div>
      )}

    </div>

  </motion.div>

);



const ProjectsGrid = ({ navigate, featured = false }) => {

  const sectionAnimation = useAnimatedSection();

  const projectsToShow = featured ? allProjects.slice(0, 3) : allProjects;

  return (

    <motion.section id="projects" className="py-8 md:py-12 bg-gray-50" {...sectionAnimation}>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8">

        {featured && <h2 className="text-3xl md:text-4xl font-bold text-gray-900 text-center mb-12 serif-font">Projects</h2>}

        <motion.div className="max-w-4xl mx-auto flex flex-col gap-6" variants={cardGridVariant} initial="hidden" animate="visible">

          {projectsToShow.map((project) => (<ProjectCard key={project.name} {...project} brief={featured} />))}

        </motion.div>

        {featured && allProjects.length > 3 && (

          <div className="text-center mt-12">

            <motion.button onClick={() => navigate('projects')} className="inline-flex items-center px-8 py-3 bg-gray-800 text-white font-semibold rounded-lg shadow-md hover:bg-black transition-colors" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>

              View All Projects

              <ArrowRight className="ml-2" size={20} />

            </motion.button>

          </div>

        )}

      </div>

    </motion.section>

  );

};



const BlogCard = ({ title, content, tags, links }) => (

  <motion.div
    className="bg-white rounded-lg overflow-hidden shadow-md hover:shadow-xl border border-gray-200 group"
    variants={cardVariant}
    whileHover={{ y: -4, transition: { duration: 0.3 } }}
  >

    <div className="p-6 md:p-8">

      <div className="flex flex-wrap gap-2 mb-4">
        {tags.map(tag => (<span key={tag} className="px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full font-medium">{tag}</span>))}
      </div>

      <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3 serif-font group-hover:text-orange-600 transition-colors duration-300">{title}</h3>

      <p className="text-gray-600 text-base leading-relaxed">{content}</p>

      {links && links.length > 0 && (
        <div className="flex flex-wrap gap-4 mt-5 pt-5 border-t border-gray-100">
          {links.map((link, idx) => (
            <a
              key={idx}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center text-orange-600 font-semibold hover:text-orange-800 transition-colors duration-300 text-sm"
            >
              <ExternalLink size={16} className="mr-1.5" />
              {link.label}
            </a>
          ))}
        </div>
      )}

    </div>

  </motion.div>

);



const BlogGrid = ({ navigate, featured = false }) => {

  const sectionAnimation = useAnimatedSection();

  const [filter, setFilter] = useState('All');

  const postsToShow = featured ? allPosts.slice(0, 4) : allPosts;

  const filteredPosts = filter === 'All' ? postsToShow : postsToShow.filter(post => post.category === filter);



  return (

    <motion.section id="blog" className="py-8 md:py-12 bg-white" {...sectionAnimation}>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8">

        {featured && (

          <div className="text-center max-w-3xl mx-auto">

            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 serif-font">From the Blog</h2>

            <p className="text-lg text-gray-700 mb-6">This blog is an extension of all the work that I do. Sometimes I write about security, GenAI/ML, startups, or things that I love like photography, cricket, or a thought that won't leave my head.</p>

          </div>

        )}

        <div className="flex justify-center space-x-2 sm:space-x-4 mb-8">

          <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={() => setFilter('All')} className={`px-4 py-2 rounded-md font-semibold transition-colors text-sm sm:text-base ${filter === 'All' ? 'bg-black text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}>All</motion.button>

          <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={() => setFilter('Tech')} className={`px-4 py-2 rounded-md font-semibold transition-colors text-sm sm:text-base ${filter === 'Tech' ? 'bg-black text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}>Tech</motion.button>

          <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={() => setFilter('Personal')} className={`px-4 py-2 rounded-md font-semibold transition-colors text-sm sm:text-base ${filter === 'Personal' ? 'bg-black text-white' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}>Personal</motion.button>

        </div>

        <motion.div className="max-w-4xl mx-auto flex flex-col gap-6" variants={cardGridVariant} initial="hidden" animate="visible">

          <AnimatePresence>

            {filteredPosts.map(post => <BlogCard key={post.title} {...post} />)}

          </AnimatePresence>

        </motion.div>

        {featured && allPosts.length > 4 && (

          <div className="text-center mt-12">

            <motion.button onClick={() => navigate('blog')} className="inline-flex items-center px-8 py-3 bg-gray-800 text-white font-semibold rounded-lg shadow-md hover:bg-black transition-colors" whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>

              More From The Blog

              <ArrowRight className="ml-2" size={20} />

            </motion.button>

          </div>

        )}

      </div>

    </motion.section>

  );

};



const ContactSection = () => {

  const sectionAnimation = useAnimatedSection();

  const contacts = [

    { icon: Mail, text: 'Email', href: 'mailto:marrirahul33@gmail.com' },

    { icon: Linkedin, text: 'LinkedIn', href: 'https://www.linkedin.com/in/rahul-marri/' },

    { icon: SubstackIcon, text: 'Substack', href: 'https://rahulmarri.substack.com/' },

  ];

  return (

    <motion.section id="contact" className="py-8 md:py-12 bg-gray-50" {...sectionAnimation}>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">

        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 serif-font">Let's Talk</h2>

        <p className="text-lg text-gray-700 max-w-2xl mx-auto mb-10">If anything here feels familiar, sparks an idea, or makes you want to reach out, I'd love to hear from you.</p>

        <div className="flex justify-center items-start gap-8 md:gap-12">

          {contacts.map((contact, index) => (

            <motion.a key={index} href={contact.href} target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-orange-600 transition-colors duration-300 flex flex-col items-center space-y-2 group w-24" whileHover={{ scale: 1.1, y: -5 }}>

              <contact.icon size={32} />

              <span className="text-sm md:text-base">{contact.text}</span>

            </motion.a>

          ))}

        </div>

      </div>

    </motion.section>

  );

};



const Footer = () => (

  <footer className="bg-white border-t border-gray-200">

    <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6 text-center text-gray-500">

      <p>&copy; {new Date().getFullYear()} Rahul Marri, All Rights Reserved.</p>

    </div>

  </footer>

);
